/**
 * storyDraft.js — REVEAL-002 R1 server-side draft lifecycle (UNIFIED-FINISH law).
 *
 * A finish saves NOTHING. The moment generation completes, the ready story is persisted here as a
 * StoryDraft row (owner-auth, its own table — invisible to every book surface) so it survives a crash
 * AND a device switch. Save routes through the EXISTING gated save seam (saveStInterviewStory); the
 * draft is deleted ONLY AFTER that seam confirms success. Discard deletes the row (nothing persists).
 *
 * BINDING LIFECYCLE RULES (founder F-57):
 *  - NO SILENT DRAFT LOSS (note 2): only two things remove a draft row — a CONFIRMED save or an
 *    EXPLICIT discard. Never delete-then-save. Drafts COEXIST (it's a table): a new finish always
 *    CREATES a new row and never overwrites/orphans an older one; resume surfaces the MOST RECENT.
 *  - VEIL-NEVER-REPLAYS (note 3): the landing-played marker lives on the ROW (landingPlayedAt), not
 *    localStorage, so a device-switch resume reads the same "already shown" state. R1 sets it on first
 *    story_ready view; R3's veil reads it and skips when set.
 *
 * The graphql-calling wrappers take the Amplify `client` so this module stays thin; the decision logic
 * (buildDraftInput / pickActiveDraft / shouldReplayLanding) is PURE and unit-tested (storyDraft.test.js).
 */

import { createStoryDraft, updateStoryDraft, deleteStoryDraft } from '../graphql/mutations';
import { listStoryDrafts } from '../graphql/queries';

// ── PURE decision logic (unit-tested) ───────────────────────────────────────

/**
 * Map the generated review-screen story (stProcessed) + interview context into a StoryDraft create
 * input. Captures BOTH polished versions + enough linkage to complete a later save. The top-level
 * title/content mirror the active version (so a resume rehydrates stProcessed directly).
 */
export function buildDraftInput(stProcessed, context = {}) {
  const p = stProcessed || {};
  const versions = p.versions || {};
  const whole = versions.whole || {};
  const flick = versions.flick || {};
  const activeVersion = p.activeVersion || 'whole';
  return {
    userId: context.userId || '',
    title: p.title || (activeVersion === 'flick' ? flick.title : whole.title) || 'My Story',
    wholeTitle: whole.title || null,
    wholeContent: whole.content || null,
    flickTitle: flick.title || null,
    flickContent: flick.content || null,
    activeVersion,
    promptId: context.promptId || null,
    promptText: context.promptText || null,
    packId: context.packId || null,
    bookId: context.bookId || null,
    occasion: context.occasion || null,
    voiceConversationId: context.voiceConversationId || null,
    polishTier: p.polishTier || null,
    // landingPlayedAt intentionally omitted on create — set on first story_ready view (markLandingPlayed).
  };
}

/**
 * F-383 — the teller's turns, serialized for the draft row. Lossless on ROLE, ORDER and TEXT, which is
 * the whole requirement: this has to round-trip back into `stInterviewMessages` intact or a resume
 * rebuilds a conversation that is not the one they had.
 *
 * Only `role` and `content` survive by design. Anything else on a turn is client render state, and
 * carrying it would make the payload grow without making the resume better.
 */
export function serializeTranscript(conversation) {
  const turns = (conversation || [])
    .filter((t) => t && typeof t.content === 'string' && t.content.trim())
    .map((t) => ({ role: t.role === 'user' ? 'user' : 'assistant', content: t.content }));
  return turns.length ? JSON.stringify(turns) : null;
}

/**
 * Parse a serialized transcript back into conversation turns. FAILS SOFT to an empty array: a draft
 * whose transcript cannot be read must not throw inside a resume and strand the teller on a blank
 * screen — the same reason the field is `String` and not `AWSJSON`.
 */
export function parseTranscript(raw) {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((t) => t && typeof t.content === 'string')
      .map((t) => ({ role: t.role === 'user' ? 'user' : 'assistant', content: t.content }));
  } catch (e) {
    return [];
  }
}

/**
 * F-383 — the PRE-GENERATION draft input. The teller's words and nothing else: there is no story yet,
 * and inventing a title here would put a machine's words where theirs belong. `title` and
 * `activeVersion` are non-null in the schema, so they carry the same placeholders `buildDraftInput`
 * already uses; `draftToProcessed` never surfaces this title, because a words-only draft resumes into
 * the INTERVIEW, not a review screen.
 */
export function buildPreGenerationDraftInput(conversation, context = {}) {
  return {
    userId: context.userId || '',
    title: 'My Story',
    activeVersion: 'whole',
    transcript: serializeTranscript(conversation),
    promptId: context.promptId || null,
    promptText: context.promptText || null,
    packId: context.packId || null,
    bookId: context.bookId || null,
    occasion: context.occasion || null,
    voiceConversationId: context.voiceConversationId || null,
  };
}

/**
 * Is this a draft that holds WORDS but no story yet? Decides which way a resume goes.
 * A draft carrying story content is a finished generation and keeps the existing State-0 behaviour.
 */
export function isWordsOnlyDraft(draft) {
  if (!draft) return false;
  const hasStory = !!(draft.wholeContent || draft.flickContent);
  return !hasStory && parseTranscript(draft.transcript).length > 0;
}

/**
 * F-402 — the words-only RESUME NOTICE. FOUNDER WORD-GATED, §10, approved VERBATIM 2026-08-06.
 * Recorded in SESSION_LEDGER; do not reword, soften or "improve" it without a new founder gate.
 *
 * PLACEMENT, approved with the words: Pip's OWN FINAL TURN at the END of the restored thread, never
 * a banner. A restored thread scrolls to the bottom, so a top banner is invisible on exactly the long
 * conversations most worth restoring. (The existing "Your story is still here" ribbon at
 * `StRecordPage.js:1711` does not cover this case — it renders on the REVIEW surface, which a
 * words-only resume deliberately never reaches.)
 *
 * WHY IT LIVES HERE and not in `interviewEngine.js` with the other spoken constants: the engine file
 * is byte-mirrored to the voice adapter, and voice has no resume path — putting a text-lane-only
 * string there would force a `deploy:voice` sync for a line voice can never speak. This module
 * already owns the words-only resume decision and is importable by Jest, which the engine's caller
 * (App.js) is not.
 */
export const RESUME_NOTICE_LINE =
  "I still have everything you told me. Let's try that again whenever you're ready.";

/**
 * Build the DISPLAY thread for a words-only resume: the teller's restored turns, then Pip's notice.
 *
 * ⚠️ DISPLAY ONLY. The returned array must never be fed to `serializeTranscript`,
 * `buildPreGenerationDraftInput`, or the assembler. App.js keeps two states for exactly this reason
 * (`stInterviewMessages` = display, `interviewConversation` = engine), and the F-7 chapter-update path
 * already uses the same split in the opposite direction. If the notice ever reaches the engine
 * conversation, the assembler receives Pip reassuring the teller as though it were interview
 * material, and it can surface in a printed keepsake.
 *
 * IDEMPOTENT: a thread already ending in the notice is returned unchanged, so a second resume (a
 * device switch, a reload) cannot stack two copies. Never mutates the input — the caller's array is
 * the engine copy and must stay clean.
 */
export function buildResumeThread(transcript) {
  const turns = Array.isArray(transcript) ? transcript : [];
  if (!turns.length) return [];
  const last = turns[turns.length - 1];
  if (last && last.content === RESUME_NOTICE_LINE) return [...turns];
  return [...turns, { role: 'assistant', content: RESUME_NOTICE_LINE }];
}

/**
 * Coexistence rule (note 2): given all of a user's drafts, return the one to RESUME — the most recent
 * by updatedAt (falling back to createdAt). Never mutates; older drafts are left intact.
 */
export function pickActiveDraft(drafts) {
  const list = (drafts || []).filter(Boolean);
  if (!list.length) return null;
  const ts = (d) => Date.parse(d.updatedAt || d.createdAt || 0) || 0;
  return list.reduce((best, d) => (ts(d) >= ts(best) ? d : best), list[0]);
}

/**
 * Veil-replay guard (note 3): the R3 landing plays only when it has NEVER been marked on this row.
 * A resume/re-entry (even a device switch) reads landingPlayedAt from the row and never replays.
 */
export function shouldReplayLanding(draft) {
  return !!draft && !draft.landingPlayedAt;
}

/**
 * REVEAL-002 R3 — derive the veil's opening line: the teller's OWN first substantive answer, first
 * sentence, VERBATIM (never paraphrased). Skips greeting/preamble turns; clamps gracefully on a WORD
 * boundary with an ellipsis (never mid-word). Returns null when nothing usable (empty/whitespace) →
 * the veil then shows Pip + the note only. Only ever needed on a FRESH finish-arrival (never on resume),
 * so it lives on stProcessed (client), not the draft row.
 *
 * DERIVATION RULE (reported):
 *  1. Walk the conversation's USER turns in order; take the FIRST that is not a greeting/preamble
 *     (a greeting = <= 6 words AND matches hi/hello/hey/ok/okay/sure/yeah/thanks/ready/let's see…).
 *     If every turn is a greeting, fall back to the first non-empty user turn.
 *  2. Take that answer's FIRST SENTENCE (up to the first . ! or ?), verbatim.
 *  3. If longer than ~120 chars, clip at the last word boundary before 120 (never mid-word), strip a
 *     dangling , ; : and append an ellipsis.
 */
const GREETING_RE = /^(hi|hello|hey|ok|okay|okey|sure|yeah|yep|yes|thanks|thank you|ready|alright|all right|let'?s see|um+|uh+|hmm+)\b/i;
export function deriveTellerFirstLine(conversation) {
  const userTurns = (conversation || [])
    .filter(t => t && t.role === 'user' && typeof t.content === 'string' && t.content.trim())
    .map(t => t.content.trim());
  if (!userTurns.length) return null;
  const isGreeting = (s) => s.split(/\s+/).length <= 6 && GREETING_RE.test(s);
  const answer = userTurns.find(s => !isGreeting(s)) || userTurns[0];
  if (!answer) return null;
  const firstSentence = (answer.match(/^[^.!?]*[.!?]?/) || [answer])[0].trim();
  if (!firstSentence) return null;
  const MAX = 120;
  if (firstSentence.length <= MAX) return firstSentence;
  const clipped = firstSentence.slice(0, MAX);
  const lastSpace = clipped.lastIndexOf(' ');
  const base = (lastSpace > 40 ? clipped.slice(0, lastSpace) : clipped).replace(/[,;:]+$/, '').trim();
  return base + '…';
}

/**
 * RESUME-LANDS-ON-A-RENDERED-SURFACE-1 — where a resumed draft must LAND.
 *
 * Returns `{ tab, step, wordsOnly }`, or null when there is nothing resumable.
 *
 * WHY THIS EXISTS AS A FUNCTION. `StRecordPage` renders only when `activeTab === 'st-record'`, and
 * `activeTab` rehydrates from localStorage — so setting `stInterviewMode`/`stInterviewStep` without
 * ALSO setting the tab produces a resume that is fetched, rehydrated, and never drawn. App.js had two
 * resume branches and only the words-only one navigated (F-402 added it there; the story-bearing
 * branch was left alone). The tab is therefore not the caller's business to remember: both branches
 * read it from here, and `storyDraft.test.js` pins it for both. App.js does not load in Jest, which is
 * why the asymmetry could sit in shipped code with a green suite.
 *
 * MEASURED COST OF THE GAP (2026-08-12): seven customers with a finished story in `StoryDraft` and
 * nothing in their book, including one who wrote to support saying the story "never showed."
 *
 * An EMPTY draft (no story, no transcript) returns null deliberately — navigating to a review with
 * nothing in it is the same blank-screen loss `draftToProcessed` refuses to produce (F-383).
 */
export function resumeTargetFor(draft) {
  if (!draft) return null;
  if (isWordsOnlyDraft(draft)) return { tab: 'st-record', step: 'conversation', wordsOnly: true };
  if (draft.wholeContent || draft.flickContent) return { tab: 'st-record', step: 'review', wordsOnly: false };
  return null;
}

/** Rehydrate a stProcessed-shaped object from a draft row (for resume → State 0). */
export function draftToProcessed(draft) {
  if (!draft) return null;
  // F-383: a WORDS-ONLY draft (generation never completed) has no story to show. Returning the old
  // shape here would hand the caller `content: ''` and land the teller on a blank review screen —
  // which is the loss this feature exists to prevent, wearing the costume of a fix. It returns a
  // transcript instead, and the caller resumes the INTERVIEW.
  if (isWordsOnlyDraft(draft)) {
    return {
      draftId: draft.id,
      wordsOnly: true,
      transcript: parseTranscript(draft.transcript),
      title: '',
      content: '',
      versions: {},
      activeVersion: 'whole',
      polishTier: null,
      saved: false,
      fromInterview: true,
      landingPlayedAt: draft.landingPlayedAt || null,
    };
  }
  const activeVersion = draft.activeVersion || 'whole';
  const versions = {};
  if (draft.wholeContent != null) versions.whole = { title: draft.wholeTitle || draft.title, content: draft.wholeContent };
  if (draft.flickContent != null) versions.flick = { title: draft.flickTitle || draft.title, content: draft.flickContent };
  const active = versions[activeVersion] || versions.whole || versions.flick || {};
  return {
    draftId: draft.id,
    title: active.title || draft.title,
    content: active.content || '',
    versions,
    activeVersion,
    polishTier: draft.polishTier || null,
    saved: false,           // a draft is, by law, NOT saved
    fromInterview: true,
    landingPlayedAt: draft.landingPlayedAt || null,
  };
}

// ── THIN graphql wrappers (integration; take the Amplify client) ─────────────

/** Create a draft row. Always creates NEW (never overwrites — coexistence). Returns the row. */
export async function persistDraft(client, stProcessed, context) {
  const input = buildDraftInput(stProcessed, context);
  const res = await client.graphql({ query: createStoryDraft, variables: { input } });
  return res.data.createStoryDraft;
}

/**
 * F-383 — create the PRE-GENERATION draft. Returns the row (caller keeps `id` for the later update).
 *
 * The caller must never let this block generation; that is enforced at the call site, not here.
 */
export async function persistPreGenerationDraft(client, conversation, context) {
  const input = buildPreGenerationDraftInput(conversation, context);
  const res = await client.graphql({ query: createStoryDraft, variables: { input } });
  return res.data.createStoryDraft;
}

/**
 * F-383 D3 — ONE ROW PER INTERVIEW. When a pre-generation draft already exists, the completed story
 * UPDATES that row instead of creating a second one.
 *
 * Why this matters beyond tidiness: `storyDraft.js` note 2 makes every finish CREATE a row, so a
 * pre-generation write plus the existing post-generation `persistDraft` would produce TWO rows per
 * interview. That doubles the row rate against `fetchActiveDraft`'s 100-item page and makes
 * DRAFT-RESUME-CLOBBER materially more reachable. It would also leave a stale words-only row behind
 * that `removeDraft` never clears on save, so a teller who saved successfully would still be offered
 * a resume of the conversation they just finished.
 *
 * The transcript is deliberately PRESERVED on update rather than nulled: it costs one field and it is
 * the teller's own words, so there is no reason to throw them away the moment a story exists.
 */
export async function updateDraftWithStory(client, draftId, stProcessed, context) {
  const input = { ...buildDraftInput(stProcessed, context), id: draftId };
  delete input.userId;   // immutable on update; owner-auth already scopes the row
  const res = await client.graphql({ query: updateStoryDraft, variables: { input } });
  return res.data.updateStoryDraft;
}

/** Update a draft's editable fields (title/content/activeVersion) — Edit-before-save updates the row. */
export async function saveDraftEdits(client, draftId, fields) {
  const res = await client.graphql({ query: updateStoryDraft, variables: { input: { id: draftId, ...fields } } });
  return res.data.updateStoryDraft;
}

/** Mark the landing as played on the ROW (R1 sets; R3 reads). Idempotent-safe to call once per arrival. */
export async function markLandingPlayed(client, draftId, whenISO) {
  const res = await client.graphql({ query: updateStoryDraft, variables: { input: { id: draftId, landingPlayedAt: whenISO } } });
  return res.data.updateStoryDraft;
}

/** Delete a draft row. Called ONLY after a confirmed save, or on explicit discard. */
export async function removeDraft(client, draftId) {
  await client.graphql({ query: deleteStoryDraft, variables: { input: { id: draftId } } });
}

/** List the caller's drafts (owner-auth auto-scopes) and return the most recent to resume, or null. */
export async function fetchActiveDraft(client) {
  const res = await client.graphql({ query: listStoryDrafts, variables: { limit: 100 } });
  return pickActiveDraft(res.data.listStoryDrafts.items);
}
